/**
 * 만공차등 테스트 — CCTV 마다 연결 주차면을 모두 점유(만차)하면 만공차등이 꺼지고, 한 면이라도
 * 비면(공차) 켜지는지 본다. 기대 상태(DB 점유로 판정)와 조명 서비스가 알려 준 실제 상태를 나란히 둔다.
 */
import React from 'react';
import { Box, Button, Chip, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';

import type {
  ParkingQaCctv,
  ParkingQaSpace,
  ParkingQaStatus,
  QaCctvLights,
} from '../../services/SiteParkingQaService';

interface Props {
  cctvs: ParkingQaCctv[];
  pending: Set<number>;
  /** 이 층 조명 (자동 갱신). 아직 못 읽었거나 조명 서비스가 없으면 undefined. */
  lights?: Record<string, QaCctvLights>;
  query: string;
  onQuery: (q: string) => void;
  onToggle: (space: ParkingQaSpace) => void;
  onApply: (ids: number[], status: ParkingQaStatus) => void;
}

/** 시뮬레이터 만공차 체크와 같은 규칙 — 면이 모두 점유면 만차. */
export const isFull = (c: ParkingQaCctv) =>
  c.spaces.length > 0 && c.spaces.every((s) => s.status === 'Y');

/** 실제 만공차등 — 켜짐/꺼짐, 이 CCTV 에 만공차등이 없으면 null. */
const actualOn = (l?: QaCctvLights) => (l && l.parkingTotal > 0 ? l.parkingOn > 0 : null);

const QaOccupancyLightPanel: React.FC<Props> = ({
  cctvs,
  pending,
  lights,
  query,
  onQuery,
  onToggle,
  onApply,
}) => (
  <Stack spacing={1.5}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mr: 'auto' }}>
        CCTV 별 만공차등
      </Typography>
      <TextField
        size="small"
        label="CCTV·주차면 검색"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        sx={{ width: { xs: 170, sm: 220 } }}
      />
    </Stack>
    <Typography variant="caption" color="text.secondary">
      전체 점유하면 만차(소등), 한 면이라도 비우면 공차(점등)가 기대 상태입니다. 실제 상태는 자동
      갱신으로 조명 서비스에서 읽습니다 — 조명이 바뀌는 데 몇 초 걸립니다.
    </Typography>
    {cctvs.length === 0 && (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        조건에 맞는 CCTV 가 없습니다.
      </Typography>
    )}
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
      }}
    >
      {cctvs.map((c) => {
        const full = isFull(c);
        const occupied = c.spaces.filter((s) => s.status === 'Y').length;
        const ids = c.spaces.map((s) => s.id);
        const busy = !ids.length || ids.some((id) => pending.has(id));
        const actual = lights ? actualOn(lights[c.name]) : undefined;
        const match = actual === undefined || actual === null ? null : actual === !full;
        return (
          <Paper
            key={c.id}
            variant="outlined"
            aria-label={`CCTV ${c.name}`}
            sx={{
              p: 1.5,
              borderRadius: 2,
              borderColor: match === false ? 'error.main' : c.held ? 'warning.main' : 'divider',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mr: 'auto' }}>
                {c.name}
              </Typography>
              {c.held && <Chip size="small" color="warning" label="카메라 반영 중지" />}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {`점유 ${occupied} / ${c.spaces.length}면`}
            </Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                variant="outlined"
                color={full ? 'error' : 'success'}
                label={full ? '기대: 만차(소등)' : '기대: 공차(점등)'}
              />
              <Chip
                size="small"
                data-testid="qa-light-actual"
                color={actual === true ? 'success' : 'default'}
                variant={actual === true ? 'filled' : 'outlined'}
                label={
                  actual === undefined
                    ? '실제: 읽는 중'
                    : actual === null
                      ? '실제: 만공차등 없음'
                      : actual
                        ? '실제: 점등'
                        : '실제: 소등'
                }
              />
              {match !== null && (
                <Chip
                  size="small"
                  data-testid="qa-light-match"
                  color={match ? 'success' : 'error'}
                  label={match ? '일치' : '불일치'}
                />
              )}
            </Stack>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, my: 1 }}>
              {c.spaces.map((s) => {
                const taken = s.status === 'Y';
                return (
                  <Tooltip
                    key={s.id}
                    title={s.isFixed ? '고정 주차면 — 카메라와 무관하게 이 값이 유지됩니다' : ''}
                  >
                    <Chip
                      size="small"
                      clickable
                      disabled={pending.has(s.id)}
                      color={taken ? 'error' : 'success'}
                      variant={s.isFixed ? 'outlined' : 'filled'}
                      label={s.name}
                      aria-label={`${s.name} ${taken ? '점유' : '빈자리'}`}
                      onClick={() => onToggle(s)}
                    />
                  </Tooltip>
                );
              })}
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                color="error"
                disabled={busy}
                onClick={() => onApply(ids, 'Y')}
                aria-label={`${c.name} 전체 점유`}
              >
                전체 점유 (만차)
              </Button>
              <Button
                size="small"
                color="success"
                disabled={busy}
                onClick={() => onApply(ids, 'N')}
                aria-label={`${c.name} 전체 비움`}
              >
                전체 비움 (공차)
              </Button>
            </Stack>
          </Paper>
        );
      })}
    </Box>
  </Stack>
);

export default QaOccupancyLightPanel;
